import React, { createContext, useContext, useState, useMemo, useCallback } from "react";

import { Booking } from "../../../models/booking";
import { CartonLog } from "../../../models/carton.log";
import { Error } from "../../../models/error";
import { BookingStatus } from "../../../util/enums";
import { BookingChangeKeys, BookingChangeOptions, BookingObjects } from "../util";

interface BookingContextProps {
  booking?: Booking;
  setBooking: React.Dispatch<React.SetStateAction<Booking>>;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  changedOptions: BookingChangeKeys;
  handleChangeOptions: (newOption: BookingChangeOptions, noChanges?: boolean) => void;
  handleUpdateBooking: (attribute: string, value: any) => void;
  bookingAlreadySubmitted: boolean;
  cartonLogs: CartonLog[];
  setCartonLogs: React.Dispatch<React.SetStateAction<CartonLog[]>>;
  hasConfirmedCBMAndWeight: boolean;
  setHasConfirmedCBMAndWeight: React.Dispatch<React.SetStateAction<boolean>>;
  responseError?: Error;
  setResponseError: (error?: Error) => void;
}

const BookingContext = createContext<BookingContextProps>({} as BookingContextProps);

interface BookingProviderProps {
  children: React.ReactChild;
}

const BookingProvider = ({ children }: BookingProviderProps) => {
  const [booking, setBooking] = useState<Booking>({
    statusShipment: BookingStatus.PENDING,
  } as Booking);
  const [cartonLogs, setCartonLogs] = useState<CartonLog[]>([]);
  const [hasConfirmedCBMAndWeight, setHasConfirmedCBMAndWeight] = useState<boolean>(false);

  const [changedOptions, setChangedOptions] = useState<BookingChangeKeys>({
    [BookingObjects.BOOKING]: false,
    [BookingObjects.CONTAINERS]: false,
    [BookingObjects.MILESTONES]: false,
    [BookingObjects.PURCHASE_ORDERS]: false,
    [BookingObjects.CARTONS]: false,
    [BookingObjects.DOCUMENTS]: false,
    [BookingObjects.MARK_AND_NUMBERS]: false,
    [BookingObjects.COMMERCIAL_INVOICE]: false,
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [responseError, setResponseError] = useState<Error>();

  const bookingAlreadySubmitted = useMemo(
    () =>
      booking.statusShipment === BookingStatus.SUBMITTED ||
      booking.statusShipment === BookingStatus.CONFIRMED ||
      booking.statusShipment === BookingStatus.CFS_REVIEW ||
      booking.statusShipment === BookingStatus.CFS_RECEIVED ||
      booking.statusShipment === BookingStatus.RECEIVED,
    [booking.statusShipment]
    
  );

  const handleChangeOptions = useCallback(
    (newOption: BookingChangeOptions, noChanges?: boolean) => {
      if (!noChanges) setHasUnsavedChanges(true);

      if (!changedOptions[newOption])
        setChangedOptions((prev) => {
          return {
            ...prev,
            [newOption]: true,
          };
        });
    },
    [changedOptions]
  );

  const handleUpdateBooking = useCallback((attribute: string, value: any) => {
    if (attribute && attribute !== "all") {
      if (!["string", "number", "boolean", "object"].includes(typeof value))
        setBooking((state: any) => ({ ...state, [attribute]: [...value] }));
      else setBooking((state: any) => ({ ...state, [attribute]: value }));
    } else {
      setBooking({ ...value });
    }
  }, []);

  return (
    <BookingContext.Provider
      value={{
        booking,
        setBooking,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        changedOptions,
        handleChangeOptions,
        handleUpdateBooking,
        bookingAlreadySubmitted,
        cartonLogs,
        setCartonLogs,
        hasConfirmedCBMAndWeight,
        setHasConfirmedCBMAndWeight,
        responseError,
        setResponseError,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

const useBookingContext = () => {
  const context = useContext(BookingContext);

  return context;
};

export { BookingContext, BookingProvider, useBookingContext };
